<app-ui-page-header title="Geral" subtitle="Dados da organização, preferências e calendários comerciais." />

<div class="mt-6 grid gap-6">
  <section class="grid gap-4 border-t border-line pt-5 first:border-t-0 first:pt-0">
    <app-ui-section-header
      label="Organização"
      help="Nome público e dados usados como base para fluxos filhos."
    >
      <button
        sectionActions
        appButton
        type="button"
        variant="ghost"
        (click)="load()"
        [disabled]="loading()"
      >
        <ng-icon name="heroArrowPath" size="16" aria-hidden="true" />
        Atualizar
      </button>
    </app-ui-section-header>

    <form
      class="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto]"
      [formGroup]="businessForm"
      (ngSubmit)="saveBusiness()"
    >
      <label class="grid gap-1.5">
        <span appUiLabel>Nome da organização</span>
        <input
          appInput
          formControlName="name"
          autocomplete="organization"
          [readonly]="!canUpdateBusiness()"
          [attr.title]="!canUpdateBusiness() ? writePermissionHint : null"
        />
      </label>
      <div class="flex items-end">
        <button
          appButton
          type="submit"
          variant="primary"
          [disabled]="saving() || businessForm.invalid || !canUpdateBusiness()"
          [attr.title]="!canUpdateBusiness() ? writePermissionHint : null"
        >
          <ng-icon name="heroCheck" size="16" aria-hidden="true" />
          Salvar
        </button>
      </div>
    </form>

    @if (businessPaused()) {
      <div
        appUiPanelCard
        class="flex flex-wrap items-center justify-between gap-3 bg-soft"
      >
        <div class="min-w-0">
          <p appUiPanelTitle>Operações pausadas</p>
          <p appUiPanelText>
            Você ainda pode acessar estas configurações para recuperar a organização.
          </p>
        </div>
        <button
          appButton
          type="button"
          variant="primary"
          (click)="reactivateBusiness()"
          [disabled]="saving() || !canUpdateBusiness()"
          [attr.title]="!canUpdateBusiness() ? writePermissionHint : null"
        >
          <ng-icon name="heroCheck" size="16" aria-hidden="true" />
          Reativar organização
        </button>
      </div>
    }
  </section>

  <section class="grid gap-4 border-t border-line pt-5 first:border-t-0 first:pt-0">
    <app-ui-section-header
      label="Preferências"
      help="Idioma, fuso horário e moeda usados por padrão na organização e em novas entidades filhas."
    />

    <form
      class="grid gap-4 md:grid-cols-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_minmax(0,1fr)_auto]"
      [formGroup]="settingsForm"
      (ngSubmit)="saveSettings()"
    >
      <div class="grid gap-1.5">
        <span appUiLabel id="locale-label">Idioma</span>
        <div
          appSelect
          aria-labelledby="locale-label"
          [ngpSelectValue]="settingsForm.controls.locale.value"
          [ngpSelectDisabled]="!canUpdateBusiness()"
          (ngpSelectValueChange)="selectLocale($event)"
          [attr.title]="!canUpdateBusiness() ? writePermissionHint : null"
        >
          <span>{{ optionLabel('locales', settingsForm.controls.locale.value) }}</span>
          <div *ngpSelectPortal appSelectDropdown>
            @for (locale of options().locales; track locale.value) {
              <div appSelectOption [ngpSelectOptionValue]="locale.value">{{ locale.label }}</div>
            }
          </div>
        </div>
      </div>

      <div class="grid gap-1.5">
        <span appUiLabel id="timezone-label"
          >Fuso horário padrão</span
        >
        <div
          appSelect
          aria-labelledby="timezone-label"
          [ngpSelectValue]="settingsForm.controls.timezone.value"
          [ngpSelectDisabled]="!canUpdateBusiness()"
          (ngpSelectValueChange)="selectTimezone($event)"
          [attr.title]="!canUpdateBusiness() ? writePermissionHint : null"
        >
          <span>{{ optionLabel('timezones', settingsForm.controls.timezone.value) }}</span>
          <div *ngpSelectPortal appSelectDropdown>
            @for (timezone of options().timezones; track timezone.value) {
              <div appSelectOption [ngpSelectOptionValue]="timezone.value">
                {{ timezone.label }}
              </div>
            }
          </div>
        </div>
      </div>

      <div class="grid gap-1.5">
        <span appUiLabel id="currency-label">Moeda</span>
        <div
          appSelect
          aria-labelledby="currency-label"
          [ngpSelectValue]="settingsForm.controls.defaultCurrency.value"
          [ngpSelectDisabled]="!canUpdateBusiness()"
          (ngpSelectValueChange)="selectCurrency($event)"
          [attr.title]="!canUpdateBusiness() ? writePermissionHint : null"
        >
          <span>{{ optionLabel('currencies', settingsForm.controls.defaultCurrency.value) }}</span>
          <div *ngpSelectPortal appSelectDropdown>
            @for (currency of options().currencies; track currency.value) {
              <div appSelectOption [ngpSelectOptionValue]="currency.value">
                {{ currency.label }}
              </div>
            }
          </div>
        </div>
      </div>

      <div class="flex items-end">
        <button
          appButton
          type="submit"
          variant="primary"
          [disabled]="saving() || settingsForm.invalid || !canUpdateBusiness()"
          [attr.title]="!canUpdateBusiness() ? writePermissionHint : null"
        >
          <ng-icon name="heroCheck" size="16" aria-hidden="true" />
          Salvar
        </button>
      </div>
    </form>
  </section>

  <section class="grid gap-4 border-t border-line pt-5 first:border-t-0 first:pt-0">
    <app-ui-section-header
      label="Calendários comerciais"
      help="Dias úteis, horários e feriados usados por atendimento, SLA e automações."
    >
      <button
        sectionActions
        appButton
        type="button"
        variant="primary"
        (click)="openCreateCalendar()"
        [disabled]="!canUpdateBusiness()"
        [attr.title]="!canUpdateBusiness() ? writePermissionHint : null"
      >
        <ng-icon name="heroPlus" size="16" aria-hidden="true" />
        Criar calendário
      </button>
    </app-ui-section-header>

    <div appUiDataTableShell>
      <table appUiDataTable>
        <thead appUiDataTableHead>
          <tr>
            <th appUiDataTableHeaderCell class="w-[24%]">Nome</th>
            <th appUiDataTableHeaderCell class="w-[20%]">Funcionamento</th>
            <th appUiDataTableHeaderCell class="w-[22%]">Fuso horário</th>
            <th appUiDataTableHeaderCell class="w-[12%]">Feriados</th>
            <th appUiDataTableHeaderCell class="w-[12%]">Padrão</th>
            <th appUiDataTableHeaderCell class="w-[10%] text-right">Ações</th>
          </tr>
        </thead>
        <tbody>
          @for (calendar of activeCalendars(); track calendar.id) {
            <tr appUiDataTableRow>
              <td appUiDataTableCell>
                <p class="truncate font-semibold text-ink">{{ calendar.name }}</p>
                @if (calendar.description) {
                  <p appUiHint class="mt-1 truncate">{{ calendar.description }}</p>
                }
              </td>
              <td appUiDataTableCell class="text-muted">
                <span class="block truncate">{{ calendarScheduleSummary(calendar) }}</span>
              </td>
              <td appUiDataTableCell class="text-muted">
                <span class="block truncate">{{ calendarTimezoneLabel(calendar) }}</span>
              </td>
              <td appUiDataTableCell class="text-muted">{{ holidayCount(calendar) }}</td>
              <td appUiDataTableCell>
                @if (calendar.isDefault) {
                  <span appBadge tone="accent" size="xs">Padrão</span>
                } @else {
                  <span appUiHint>Alternativo</span>
                }
              </td>
              <td appUiDataTableCell>
                <div appUiDataTableActions>
                  <button
                    appButton
                    type="button"
                    variant="secondary"
                    size="sm"
                    (click)="openEditCalendar(calendar)"
                    [disabled]="!canUpdateBusiness()"
                    [attr.title]="!canUpdateBusiness() ? writePermissionHint : null"
                    aria-label="Editar calendário"
                  >
                    <ng-icon name="heroPencil" size="15" aria-hidden="true" />
                  </button>
                  <button
                    appButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    (click)="openDeleteCalendar(calendar)"
                    [disabled]="!canUpdateBusiness()"
                    [attr.title]="!canUpdateBusiness() ? writePermissionHint : null"
                    aria-label="Excluir calendário"
                  >
                    <ng-icon name="heroTrash" size="15" aria-hidden="true" />
                  </button>
                </div>
              </td>
            </tr>
          } @empty {
            <tr>
              <td appUiDataTableEmptyCell colspan="6">
                <app-ui-empty-state
                  compact
                  title="Nenhum calendário ativo"
                  description="Calendários ativos aparecerão aqui para controlar horários de atendimento e feriados."
                />
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </section>

  @if (creatingCalendar() || editingCalendar()) {
    <div appDialogOverlay>
      <section
        appDialog
        class="flex h-[min(44rem,calc(100dvh-2rem))] !w-[min(44rem,calc(100vw-2rem))] !max-w-none flex-col overflow-hidden"
      >
        <div class="shrink-0 border-b border-line pb-4">
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 appDialogTitle class="text-base">
                {{ editingCalendar() ? 'Editar calendário' : 'Novo calendário' }}
              </h3>
              <p appDialogDescription>
                Defina o expediente recorrente e os feriados que fecham a operação.
              </p>
            </div>
            <button
              appButton
              type="button"
              variant="ghost"
              size="sm"
              (click)="closeCalendarDialog()"
              aria-label="Fechar"
            >
              <ng-icon name="heroXMark" size="16" aria-hidden="true" />
            </button>
          </div>
        </div>

        <form
          class="flex min-h-0 flex-1 flex-col"
          [formGroup]="calendarForm"
          (ngSubmit)="saveCalendar()"
        >
          <div class="min-h-0 flex-1 overflow-y-auto overflow-x-hidden py-5 pr-1">
            <div class="grid gap-4">
              <div class="grid gap-3 md:grid-cols-2">
                <label class="grid gap-1.5">
                  <span appUiLabel>Nome</span>
                  <input
                    appInput
                    formControlName="name"
                    placeholder="Atendimento comercial"
                    autocomplete="off"
                    [readonly]="!canUpdateBusiness()"
                  />
                </label>
                <div class="grid gap-1.5">
                  <span appUiLabel id="calendar-timezone-label"
                    >Fuso horário</span
                  >
                  <div
                    appSelect
                    aria-labelledby="calendar-timezone-label"
                    [ngpSelectValue]="calendarForm.controls.timezone.value"
                    [ngpSelectDisabled]="!canUpdateBusiness()"
                    (ngpSelectValueChange)="selectCalendarTimezone($event)"
                  >
                    <span>{{
                      optionLabel('timezones', calendarForm.controls.timezone.value)
                    }}</span>
                    <div *ngpSelectPortal appSelectDropdown>
                      @for (timezone of options().timezones; track timezone.value) {
                        <div appSelectOption [ngpSelectOptionValue]="timezone.value">
                          {{ timezone.label }}
                        </div>
                      }
                    </div>
                  </div>
                </div>
              </div>

              <label class="grid gap-1.5">
                <span appUiLabel>Descrição</span>
                <textarea
                  appTextarea
                  formControlName="description"
                  placeholder="Ex.: Atendimento principal de segunda a sexta."
                  [readonly]="!canUpdateBusiness()"
                ></textarea>
              </label>

              <section appUiPanelCard class="grid gap-3">
                <app-ui-section-header
                  label="Funcionamento recorrente"
                  help="Dias e horário base em que o calendário considera a operação aberta."
                />
                <div class="flex flex-wrap gap-2">
                  @for (day of weekDays; track day.index) {
                    <div
                      appUiPanelCard
                      appUiLabel
                      class="inline-flex items-center gap-2 !px-3 !py-2"
                    >
                      <button
                        appCheckbox
                        type="button"
                        [ngpCheckboxChecked]="calendarWorkingDays().includes(day.index)"
                        [ngpCheckboxDisabled]="!canUpdateBusiness()"
                        (ngpCheckboxCheckedChange)="toggleCalendarDay(day.index, $event)"
                        [attr.aria-label]="'Alternar ' + day.label"
                      >
                        <ng-icon name="heroCheck" size="13" aria-hidden="true" class="opacity-0 transition-opacity group-data-[checked]:opacity-100" />
                      </button>
                      {{ day.short }}
                    </div>
                  }
                </div>
                <div class="grid gap-3 sm:grid-cols-2">
                  <label class="grid gap-1.5">
                    <span appUiLabel>Abre às</span>
                    <input
                      appInput
                      type="time"
                      formControlName="opensAt"
                      [readonly]="!canUpdateBusiness()"
                    />
                  </label>
                  <label class="grid gap-1.5">
                    <span appUiLabel>Fecha às</span>
                    <input
                      appInput
                      type="time"
                      formControlName="closesAt"
                      [readonly]="!canUpdateBusiness()"
                    />
                  </label>
                </div>
                @if (calendarTimeInvalid()) {
                  <p class="text-xs font-semibold text-tomato">
                    O horário de fechamento precisa ser posterior ao de abertura.
                  </p>
                }
              </section>

              <section appUiPanelCard class="grid gap-3">
                <app-ui-section-header
                  label="Feriados"
                  help="Cadastre datas sem expediente para este calendário."
                >
                  <button
                    sectionActions
                    appButton
                    type="button"
                    variant="secondary"
                    size="sm"
                    (click)="addHolidayRow()"
                    [disabled]="!canUpdateBusiness()"
                  >
                    <ng-icon name="heroPlus" size="15" aria-hidden="true" />
                    Adicionar feriado
                  </button>
                </app-ui-section-header>

                <div class="grid gap-2">
                  @for (holiday of calendarHolidays(); track holiday.id) {
                    <div class="grid gap-2 sm:grid-cols-[10rem_minmax(0,1fr)_auto]">
                      <label class="grid gap-1.5">
                        <span appUiLabel>Data</span>
                        <input
                          type="date"
                          appUiDateField
                          [value]="holiday.date"
                          [readonly]="!canUpdateBusiness()"
                          [invalid]="holidayDateInvalid(holiday)"
                          (input)="updateHolidayDate(holiday.id, $any($event.target).value)"
                        />
                      </label>
                      <label class="grid gap-1.5">
                        <span appUiLabel>Nome</span>
                        <input
                          appInput
                          [value]="holiday.label"
                          placeholder="Natal"
                          [readonly]="!canUpdateBusiness()"
                          (input)="updateHolidayLabel(holiday.id, $any($event.target).value)"
                        />
                      </label>
                      <div class="flex items-end">
                        <button
                          appButton
                          type="button"
                          variant="ghost"
                          size="sm"
                          (click)="removeHolidayRow(holiday.id)"
                          [disabled]="!canUpdateBusiness()"
                          aria-label="Remover feriado"
                        >
                          <ng-icon name="heroTrash" size="15" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  } @empty {
                    <p appUiEmptyText>Nenhum feriado cadastrado.</p>
                  }
                </div>
                @if (holidayRowsInvalid()) {
                  <p class="text-xs font-semibold text-tomato">
                    Corrija ou remova feriados com data inválida.
                  </p>
                }
              </section>

              <div class="inline-flex items-center gap-2 text-sm font-semibold text-ink">
                <button
                  appCheckbox
                  type="button"
                  formControlName="isDefault"
                  [ngpCheckboxDisabled]="!canUpdateBusiness()"
                  aria-label="Usar como calendário padrão da organização"
                >
                  <ng-icon name="heroCheck" size="13" aria-hidden="true" class="opacity-0 transition-opacity group-data-[checked]:opacity-100" />
                </button>
                Usar como calendário padrão da organização
              </div>
            </div>
          </div>

          <div class="flex shrink-0 justify-end gap-2 border-t border-line pt-4">
            <button appButton type="button" variant="secondary" (click)="closeCalendarDialog()">
              Cancelar
            </button>
            <button
              appButton
              type="submit"
              variant="primary"
              [disabled]="
                saving() ||
                calendarForm.invalid ||
                calendarTimeInvalid() ||
                holidayRowsInvalid() ||
                !canUpdateBusiness()
              "
            >
              <ng-icon name="heroCheck" size="16" aria-hidden="true" />
              Salvar calendário
            </button>
          </div>
        </form>
      </section>
    </div>
  }

  @if (deletingCalendar(); as calendar) {
    <div appDialogOverlay>
      <section appDialog>
        <div class="flex items-start justify-between gap-3">
          <div>
            <p class="text-xs font-semibold uppercase tracking-wide text-accent">
              Excluir calendário
            </p>
            <h3 appDialogTitle class="mt-1 text-base">{{ calendar.name }}</h3>
            <p appDialogDescription>
              O calendário sai das listas ativas. Regras antigas que já apontavam para ele não são
              apagadas.
            </p>
            @if (calendar.isDefault) {
              <p class="mt-2 text-sm font-semibold text-tomato">
                Este é o calendário padrão da organização. A referência padrão será removida.
              </p>
            }
          </div>
          <button
            appButton
            type="button"
            variant="ghost"
            size="sm"
            (click)="closeDeleteCalendar()"
            aria-label="Fechar"
          >
            <ng-icon name="heroXMark" size="16" aria-hidden="true" />
          </button>
        </div>

        <div class="mt-5 flex justify-end gap-2">
          <button appButton type="button" variant="secondary" (click)="closeDeleteCalendar()">
            Cancelar
          </button>
          <button
            appButton
            type="button"
            variant="danger"
            (click)="confirmDeleteCalendar()"
            [disabled]="saving() || !canUpdateBusiness()"
          >
            <ng-icon name="heroTrash" size="16" aria-hidden="true" />
            Excluir
          </button>
        </div>
      </section>
    </div>
  }
</div>
